import { createContext, useCallback, useContext, useLayoutEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { getThemes, getUserTheme, saveUserTheme } from '../services/themeService';
import { applyThemeColor, readCachedTheme, writeCachedTheme } from '../theme/themeUtils';

const ThemeContext = createContext(null);

export function ThemeProvider({ children }) {
  const [currentTheme, setCurrentTheme] = useState(() => readCachedTheme());
  const [themes, setThemes] = useState([]);
  const [saving, setSaving] = useState(false);

  useLayoutEffect(() => {
    applyThemeColor(currentTheme?.colorCode);
  }, [currentTheme]);

  const loadUserTheme = useCallback(async () => {
    const cached = readCachedTheme();
    if (cached) setCurrentTheme(cached); 

    try {
      const [list, mine] = await Promise.all([getThemes(), getUserTheme()]);
      setThemes(list);
      if (mine) {
        setCurrentTheme(mine);
        writeCachedTheme(mine);
      }
    } catch (err) {
      console.error('Failed to load theme:', err);
    }
  }, []);

  const selectTheme = useCallback(
    async (themeId) => {
      if (saving) return; 
      const target = themes.find((t) => t.themeId === themeId);
      if (!target || target.themeId === currentTheme?.themeId) return;

      const previous = currentTheme;
      setCurrentTheme({ ...target, isUserSelected: true }); 
      setSaving(true);

      try {
        const saved = await saveUserTheme(themeId);
        setCurrentTheme(saved);
        writeCachedTheme(saved);
        // Same numbering as the dropdown: position in the list + 1 ("Theme 1", "Theme 2", ...)
        const themeIndex = themes.findIndex((t) => t.themeId === (saved?.themeId ?? themeId));
        const themeLabel = themeIndex >= 0 ? `Theme ${themeIndex + 1}` : 'Theme';
        toast.success(`Theme changed to ${themeLabel}`);
      } catch (err) {
        setCurrentTheme(previous); 
        toast.error(err.response?.data?.message || 'Failed to save theme. Please try again.');
        if (err.response?.status === 400) {
          getThemes().then(setThemes).catch(() => {});
        }
      } finally {
        setSaving(false);
      }
    },
    [saving, themes, currentTheme]
  );

  const resetTheme = useCallback(() => {
    setCurrentTheme(null);
    setThemes([]);
  }, []);

  const value = useMemo(
    () => ({ currentTheme, themes, saving, loadUserTheme, selectTheme, resetTheme }),
    [currentTheme, themes, saving, loadUserTheme, selectTheme, resetTheme]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export const useTheme = () => {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
};